/* eslint-disable react/jsx-boolean-value */
/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDroppable, DragOverlay } from '@dnd-kit/core';
import {
  arrayOf, string, number, func, bool, shape
} from 'prop-types';
import { DraggableCabinet } from './dnd/DraggableCabinet';
import { DragOverlayCabinet } from './dnd/DragOverlayCabinet';
import { Cabinet } from './Cabinet';
import { inchesToPxs } from '../../../../utils/wall-size-utils';
import { CabinetShape, SegmentShape } from './type';
import { TouchPoint } from './TouchPoint';
import { useTouchPointManager } from '../hooks/useTouchPointManager';
import { useCabinetVisualizerStore } from '../../../../store/index';
import { useCabinetStore, useViewportStore } from '../../../../store/ui';
import { cabinetToBoundingBox, getTotalBounding, getConstrainedWallHeight } from '../utils';
import { useWallDesigner } from '../hooks/wallDesignerHooks';
import {
  selectDesignAnchorSide,
  selectTopAndBottomProducts,
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_TOUCH_POINT_WIDTH,
  DEFAULT_TOUCH_POINT_START_COLUMN,
  getSegments
} from '../../../../store/cabinetVisualizer/selectors';
import { Segment } from './Segment';
import { DimensionArrowsContainer } from './DimensionArrowsContainer';
import { CabinetDimensions } from './CabinetDimensions';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import { DIRECTION } from '../../cabinetConstants';
import { tryGetQueryParam } from '../../../../utils/queryParams';
import { getSpacerMediaUrl } from './utils/wall-designer-cabinet-utils';

function originToScreen(origin, pxInInches) {
  const { Row, Column, Height, Width } = origin;

  return {
    Row: inchesToPxs(Row, pxInInches),
    Column: inchesToPxs(Column, pxInInches),
    Width: inchesToPxs(Width, pxInInches),
    Height: inchesToPxs(Height, pxInInches)
  };
}

function boundingToScreen(box, pxInInches) {
  const { top, left, height, width } = box;

  const result = {
    top: inchesToPxs(top, pxInInches),
    left: inchesToPxs(left, pxInInches),
    height: inchesToPxs(height, pxInInches),
    width: inchesToPxs(width, pxInInches)
  };

  if (box.right) {
    result.right = inchesToPxs(box.right, pxInInches);
  }

  if (box.bottom) {
    result.bottom = inchesToPxs(box.bottom, pxInInches);
  }

  return result;
}

function touchPointToScreen(touchPoint, pxInInches) {
  const result = boundingToScreen(touchPoint, pxInInches);
  result.type = touchPoint.type;

  return result;
}

export const Wall = React.memo(({
  rows,
  columns,
  segments,
  cabinets,
  cart,
  pxInInches,
  zoom,
  onTouchPoint,
  onTouchPointForSpacer,
  onTouchPointForFiller,
  handleQuickView,
  handleDuplicate,
  designId,
  disableTouchPoints,
  showDimensions,
  scrollOffset,
  onFloorRectChange,
  onWallContainerRefChange,
  overlayPosition,
}) => {
  const { totalWidth, totalHeight } = useWallDesigner(designId);
  const isOutOfBound = Number(totalWidth) > Number(columns) || Number(totalHeight) > Number(rows);
  const dndEnabled = !(tryGetQueryParam('dndEnabled') === 'false');
  const isZoomEnabled = tryGetQueryParam('isZoom') === 'true';
  const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';
  const isMobile = useIsMobileDevice();
  const selectedCabinet = useCabinetStore((state) => state.selectedCabinet);
  const activeCabinet = useCabinetStore((state) => state.activeCabinet);
  const viewportInfo = useViewportStore((state) => state.viewportInfo);
  const floorRef = useRef(null);
  const { setNodeRef } = useDroppable({
    id: 'wall-droppable', // Unique ID for the droppable area
  });

  const wallContainerRef = useRef(null);
  const combinedRef = (node) => {
    setNodeRef(node); // DnD Kit ref
    wallContainerRef.current = node;
    if (onWallContainerRefChange) {
      onWallContainerRefChange(node);
    }
  };
  const wallWidth = !columns ? DEFAULT_VIEWPORT_WALLSIZE.Columns : columns;
  const wallHeight = !rows || rows > DEFAULT_VIEWPORT_WALLSIZE.Rows
    ? DEFAULT_VIEWPORT_WALLSIZE.Rows : rows;

  const hasWallMeasurements = !!(columns && columns > 0 && rows && rows > 0);
  const zoomedPxInInches = zoom * pxInInches;
  const convertedWidth = inchesToPxs(wallWidth, zoomedPxInInches);
  const convertedHeight = inchesToPxs(wallHeight, zoomedPxInInches);
  const convertedGuideWidth = inchesToPxs(columns, zoomedPxInInches);
  const convertedGuideHeight = inchesToPxs(rows, zoomedPxInInches);
  const wallContainerPadding = !enableTouchPointsOnBothSides
    ? 16
    : inchesToPxs(DEFAULT_TOUCH_POINT_START_COLUMN, zoomedPxInInches);

  const baseBoardHeight = 3; // Standard base board height is 3 to 5 inches
  const convertedBaseBoardHeight = inchesToPxs(baseBoardHeight, zoomedPxInInches);

  const [currentFloorHeight, setCurrentFloorHeight] = useState(0);
  const floorHeightInInches = (isMobile ? 24 : 12) / zoom; // Default floor height in inches
  const floorHeight = isMobile ? 60 : inchesToPxs(floorHeightInInches, zoomedPxInInches);

  const {
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const anchorSide = selectDesignAnchorSide(cabinetVisualizer, designId);
  const { combinedTopProducts, combinedBottomProducts } = selectTopAndBottomProducts(cabinetVisualizer, designId);

  // Memoize spacer media URL for active cabinet to avoid recalculating on every render
  const activeSpacerMediaUrl = useMemo(
    () => getSpacerMediaUrl(activeCabinet, cabinetVisualizer),
    [activeCabinet, cabinetVisualizer]
  );

  const touchPoints = useTouchPointManager(
    cabinets, combinedTopProducts, combinedBottomProducts,
    { anchorSide, disableTouchPoints }, enableTouchPointsOnBothSides);

  // Calculate total bounding from cabinets
  const cabinetBoundings = cabinets?.map((cabinet) => cabinetToBoundingBox(cabinet)) || [];
  const totalBounding = getTotalBounding(cabinetBoundings);

  const newConvertedBounding = boundingToScreen(totalBounding, zoomedPxInInches);

  const offsetTouchPoint = inchesToPxs(DEFAULT_TOUCH_POINT_WIDTH, zoomedPxInInches);
  const isManualZoom = !!viewportInfo?.manualZoom;

  // For right-anchor at manual zoom, the wall is positioned via flex-end and extends
  // leftward past the container at higher zoom. Browsers can't scroll to negative positions,
  // so shift the wall rightward to make all content (including left touchpoints) scrollable.
  // At auto-zoom the scale already fits everything in the viewport.
  let negativeOverflowPx = 0;
  if (isZoomEnabled && cabinets.length > 0 && anchorSide === DIRECTION.RIGHT && isManualZoom) {
    const leftContentEdgePx = Math.min(0, newConvertedBounding.left - offsetTouchPoint);
    negativeOverflowPx = Math.abs(leftContentEdgePx) + wallContainerPadding;
  }

  // For cabinets that extend above the wall zone (e.g., 42" tall wall cabinets whose
  // Origin.Row is negative), the wall-container height must grow so browsers can scroll
  // up to reveal them. Only applied in manual zoom mode where overflow:auto enables
  // scrolling. In auto-zoom mode, handleAutoZoom scales everything to fit instead.
  let negativeOverflowTopPx = 0;
  if (isManualZoom && cabinets.length > 0 && newConvertedBounding.top < 0) {
    negativeOverflowTopPx = Math.abs(newConvertedBounding.top);
  }

  const getArrowClassNameText = (index) => {
    switch (index) {
    case 0:
      return 'arrow arrow-top arrow-left';
    case 1:
      return 'arrow arrow-top arrow-right';
    case 2:
      return 'arrow arrow-bottom arrow-left';
    default:
      return 'arrow arrow-bottom arrow-right';
    }
  };
  const getArrowAltText = (index) => {
    switch (index) {
    case 0:
      return 'arrow pointing to the top left interaction point';
    case 1:
      return 'arrow pointing to the top right interaction point';
    case 2:
      return 'arrow pointing to the bottom left interaction point';
    default:
      return 'arrow pointing to the bottom right interaction point';
    }
  };
  const finalSegments = segments.length === 0 ? getSegments(columns, getConstrainedWallHeight(rows)) : segments;
  const getJustifyContent = () => {
    if (cabinets.length === 0) {
      return 'center';
    }

    if (anchorSide === 'left') {
      return 'flex-start';
    }
    return 'flex-end';
  };

  const defaultWallWidth = DEFAULT_VIEWPORT_WALLSIZE.Columns * zoomedPxInInches;
  const defaultWallHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows * zoomedPxInInches;

  const constrainedHeight = useMemo(() => {
    if (cabinets.length === 0) return defaultWallHeight;
    return convertedGuideHeight || convertedHeight;
  }, [cabinets.length, defaultWallHeight, convertedGuideHeight, convertedHeight]);

  const constrainedWallWidth = useMemo(() => {
    if (!hasWallMeasurements) return defaultWallWidth;
    return convertedGuideWidth || convertedWidth;
  }, [hasWallMeasurements, defaultWallWidth, convertedGuideWidth, convertedWidth]);

  // Pre-compute wall-guide horizontal position based on anchor side and overflow state.
  // Left-anchor: always use left positioning relative to the shifted wall.
  // Right-anchor with overflow: use left positioning computed from the wall's right edge.
  // Right-anchor without overflow: use right positioning (original behavior).
  const wallGuidePosition = useMemo(() => {
    if (anchorSide === DIRECTION.LEFT) {
      return { left: negativeOverflowPx + wallContainerPadding + offsetTouchPoint };
    }
    if (negativeOverflowPx > 0) {
      return { left: negativeOverflowPx + defaultWallWidth - offsetTouchPoint - constrainedWallWidth };
    }
    return { right: wallContainerPadding + offsetTouchPoint };
  }, [anchorSide, negativeOverflowPx, wallContainerPadding, offsetTouchPoint,
    defaultWallWidth, constrainedWallWidth]);

  const baseZoom = viewportInfo?.baseZoom || 1;
  const zoomScaleFactor = viewportInfo?.manualZoom ? zoom / baseZoom : 1;
  const viewportWidth = viewportInfo?.width || 0;
  const computedFloorWidth = Math.max(
    viewportWidth * zoomScaleFactor + negativeOverflowPx,
    viewportWidth
  );
  const floorBackgroundWidth = isZoomEnabled && isManualZoom && zoomScaleFactor > 1 && computedFloorWidth > 0
    ? `max(100%, ${Math.max(
      computedFloorWidth,
      convertedWidth + wallContainerPadding * 2,
      defaultWallWidth + wallContainerPadding
    )}px)`
    : '100%';

  const floorBackgroundStyles = {
    floorBackground: {
      width: floorBackgroundWidth,
      height: floorHeight
    },
    baseBoardBackground: {
      width: floorBackgroundWidth,
      bottom: floorHeight,
      height: convertedBaseBoardHeight,
    }
  };

  useEffect(() => {
    if (onFloorRectChange && floorRef.current) {
      if (currentFloorHeight !== floorHeight) {
        const rect = floorRef.current.getBoundingClientRect();
        onFloorRectChange(rect);
        setCurrentFloorHeight(floorHeight);

      }
    }
  }, [floorHeight]);

  return (
    <div
      className="wall-container"
      style={{
        width: negativeOverflowPx > 0
          ? `max(100%, ${negativeOverflowPx + defaultWallWidth}px)`
          : '100%',
        marginTop: `${60 + negativeOverflowTopPx}px`,
        height: convertedHeight + floorHeight + negativeOverflowTopPx,
      }}
      ref={combinedRef} // Attach droppable ref
    >
      <div
        className="wall-baseboard-background"
        style={floorBackgroundStyles.baseBoardBackground}
      />
      <div
        ref={floorRef}
        className="wall-floor-background"
        style={floorBackgroundStyles.floorBackground}
      />
      <div
        className="wall-content"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: getJustifyContent(),
          ...(cabinets.length > 0 && anchorSide === 'left' && { paddingLeft: `${wallContainerPadding}px` }),
          ...(cabinets.length > 0 && anchorSide === 'right' && { paddingRight: `${wallContainerPadding}px` }),

        }}
      >
        <div
          key={`wall-container-${cabinets?.[0]?.id || 'empty'}-${cabinets?.length || 0}-${cart?.length || 0}`}
          className="wall"
          style={{
            background: 'none',
            position: 'absolute',
            zIndex: 3,
            bottom: floorHeight,
            ...(negativeOverflowPx > 0 && { left: negativeOverflowPx }),
            width: defaultWallWidth,
            height: defaultWallHeight,
          }}
        >
          {showDimensions && (
            <DimensionArrowsContainer
              designId={designId}
              rows={wallHeight}
              columns={wallWidth}
              zoom={zoom}
              pxInInches={pxInInches}
              anchorSide={anchorSide}
            />
          )}
          {!showDimensions && cabinets.length === 0 && (
            <div className="emptyDesignInstructions">
              <span className="instructionLineOne">
                Add cabinets to preview them on your wall
              </span>
              <span className="instructionLineTwo">
                Choose left or right side to start your cabinet project
              </span>
            </div>
          )}
          {showDimensions && cabinets?.map((cab) => {
            const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
            return (
              <CabinetDimensions
                key={`${cab.Sku}-${cab.Origin.Column}--${cab.Origin.Row}`}
                top={Row}
                left={Column}
                width={Width}
                height={Height}
                heightDim={cab.Origin.Height}
                widthDim={cab.Origin.Width}
              />
            );
          })}
          {!showDimensions && dndEnabled
            && cabinets?.filter((cab) => !(activeCabinet && cab.id === activeCabinet.id))
              .map((cab) => {
                const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
                const spacerMediaUrl = getSpacerMediaUrl(cab, cabinetVisualizer);
                return (
                  <DraggableCabinet
                    key={`${cab.id}-${cab.Sku}`}
                    cabinetId={cab.id}
                    top={Row}
                    left={Column}
                    width={Width}
                    height={Height}
                    itemId={cab.Sku}
                    mirror={cab.mirror}
                    handleQuickView={handleQuickView}
                    handleDuplicate={handleDuplicate}
                    designId={designId}
                    origin={cab.Origin}
                    spacer={cab.Sku > 0}
                    spacerName={cab.Name}
                    spacerMediaUrl={spacerMediaUrl}
                    cabinets={cabinets}
                    topCabinets={combinedTopProducts}
                    bottomCabinets={combinedBottomProducts}
                    anchorSide={anchorSide}
                    border={selectedCabinet?.id === cab.id && !activeCabinet}
                  />
                );
              })}
          {!showDimensions && !dndEnabled && cabinets?.map((cab) => {
            const { Row, Column, Width, Height } = originToScreen(cab.Origin, zoomedPxInInches);
            const spacerMediaUrl = getSpacerMediaUrl(cab, cabinetVisualizer);
            return (
              <Cabinet
                key={`${cab.id}-${cab.Sku}`}
                id={cab.id}
                top={Row}
                left={Column}
                width={Width}
                height={Height}
                itemId={cab.Sku}
                mirror={cab.mirror}
                handleQuickView={handleQuickView}
                handleDuplicate={handleDuplicate}
                designId={designId}
                origin={cab.Origin}
                spacer={cab.Sku > 0}
                spacerName={cab.Name}
                spacerMediaUrl={spacerMediaUrl}
                cabinets={cabinets}
                topCabinets={combinedTopProducts}
                bottomCabinets={combinedBottomProducts}
                anchorSide={anchorSide}
                border={selectedCabinet?.id === cab.id}
              />
            );
          })}
          {!showDimensions && (
            <DragOverlay
              adjustScale={true}
              dropAnimation={null}
              container={wallContainerRef.current}
            >
              {activeCabinet && overlayPosition ? (
                <DragOverlayCabinet
                  id={activeCabinet?.id}
                  left={overlayPosition?.Column || 0}
                  top={overlayPosition?.Row || 0}
                  width={overlayPosition?.Width || 0}
                  height={overlayPosition?.Height || 0}
                  mirror={activeCabinet?.mirror}
                  origin={activeCabinet?.origin}
                  itemId={activeCabinet?.Sku}
                  spacerName={activeCabinet?.Name}
                  spacerMediaUrl={activeSpacerMediaUrl}
                  voidSpacer={overlayPosition?.voidSpacer}
                />
              ) : null}
            </DragOverlay>
          )}
          {!showDimensions && touchPoints.map((curPoint, index) => {
            const convertedPoint = touchPointToScreen(curPoint, zoomedPxInInches);
            return (
              <TouchPoint
                key={`touchpoint-${index}`}
                top={convertedPoint.top}
                zoom={zoom}
                left={convertedPoint.left}
                width={convertedPoint.width}
                height={convertedPoint.height}
                onTouchPoint={(event) => onTouchPoint(event, curPoint)}
                onTouchPointForSpacer={(event) => onTouchPointForSpacer(event, curPoint)}
                onTouchPointForFiller={(event) => onTouchPointForFiller(event, curPoint)}
                showText={cabinets.length === 0}
                type={convertedPoint.type[0]}
              />
            );
          })}
        </div>
        {(finalSegments)?.map(({ Start, End, Type }) => (
          <Segment
            left={Start.Column}
            top={Start.Row}
            width={End.Column - Start.Column}
            height={End.Row - Start.Row}
            pxInInches={zoomedPxInInches}
            type={Type}
          />
        ))}
        {hasWallMeasurements && cabinets.length > 0 && (
          <div
            className="wall-guide"
            style={{
              background: isOutOfBound ? 'rgba(222,215,195)' : 'rgba(255,255,255,.2)',
              border: isOutOfBound ? '2px solid #EBB820' : 'none',
              position: 'absolute',
              zIndex: 2,
              width: constrainedWallWidth,
              height: constrainedHeight,
              bottom: floorHeight,
              ...wallGuidePosition,
            }}
          />
        )}
      </div>
    </div>
  );
});

Wall.propTypes = {
  cabinets: arrayOf(CabinetShape).isRequired,
  cart: arrayOf(shape({})),
  segments: arrayOf(SegmentShape),
  rows: number.isRequired,
  columns: number.isRequired,
  pxInInches: number.isRequired,
  zoom: number,
  onTouchPoint: func,
  onTouchPointForSpacer: func,
  onTouchPointForFiller: func,
  handleQuickView: func.isRequired,
  handleDuplicate: func.isRequired,
  designId: string.isRequired,
  showDimensions: bool,
  scrollOffset: shape({
    scrollLeft: number,
    scrollTop: number,
  }),
  onFloorRectChange: func,
  onWallContainerRefChange: func,
  disableTouchPoints: bool,
  overlayPosition: shape({
    Column: number,
    Row: number,
    Width: number,
    Height: number
  })
};

Wall.defaultProps = {
  cart: [],
  zoom: 1,
  onTouchPoint: () => { },
  onTouchPointForSpacer: () => { },
  onTouchPointForFiller: () => { },
  segments: [],
  showDimensions: false,
  scrollOffset: { scrollLeft: 0, scrollTop: 0 },
  onFloorRectChange: () => { },
  onWallContainerRefChange: () => { },
  disableTouchPoints: false,
  overlayPosition: {
    Column: 0,
    Row: 0,
    Width: 0,
    Height: 0
  }
};
